import React from 'react';
import styled from 'styled-components';

const MarkdownContainer = styled.div`
  & p {
    margin: 0 0 0.75rem 0;
    line-height: 1.6;

    &:last-child {
      margin-bottom: 0;
    }
  }

  & strong {
    font-weight: 600;
  }

  & em {
    font-style: italic;
  }

  & ul,
  & ol {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
  }

  & li {
    margin: 0.25rem 0;
    line-height: 1.6;
  }

  & a {
    color: inherit;
    text-decoration: underline;
    opacity: 0.9;

    &:hover {
      opacity: 1;
    }
  }

  & code {
    background-color: rgba(0, 0, 0, 0.1);
    padding: 0.125rem 0.25rem;
    border-radius: 0.25rem;
    font-family: 'Courier New', monospace;
    font-size: 0.875em;
  }

  & pre {
    background-color: rgba(0, 0, 0, 0.1);
    padding: 0.75rem;
    border-radius: 0.5rem;
    overflow-x: auto;
    margin: 0.5rem 0;

    & code {
      background: none;
      padding: 0;
    }
  }

  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    margin: 1rem 0 0.5rem 0;
    font-weight: 600;

    &:first-child {
      margin-top: 0;
    }
  }

  & h1 {
    font-size: 1.5rem;
  }

  & h2 {
    font-size: 1.25rem;
  }

  & h3 {
    font-size: 1.125rem;
  }

  & blockquote {
    border-left: 3px solid rgba(0, 0, 0, 0.2);
    padding-left: 1rem;
    margin: 0.5rem 0;
    font-style: italic;
  }

  & hr {
    border: none;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    margin: 1rem 0;
  }
`;

/**
 * Simple markdown renderer that converts markdown text to HTML
 */
export const parseMarkdown = (text: string): string => {
  if (!text) return '';

  let html = text;

  // Normalise numbered list markers from "1:" to "1." so ordered list regex matches
  html = html.replace(/(\d+):/g, '$1.');

  // Escape HTML first (but preserve existing HTML entities)
  html = html
    .replace(/&(?!#?[a-z0-9]+;)/gi, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Headers
  html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
  html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
  html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');

  // Bold
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');

  // Italic
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  html = html.replace(/_(.+?)_/g, '<em>$1</em>');

  // Links [text](url)
  html = html.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
  );

  // Code blocks
  html = html.replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>');

  // Inline code
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Process lists - split into lines first
  const lines = html.split('\n');
  const processedLines: string[] = [];
  let inUnorderedList = false;
  let inOrderedList = false;
  let listItems: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const unorderedMatch = line.match(/^[-*]\s+(.+)$/);
    const orderedMatch = line.match(/^\d+\.\s+(.+)$/);

    if (unorderedMatch) {
      if (!inUnorderedList) {
        if (inOrderedList) {
          // Close ordered list
          processedLines.push(`<ol>${listItems.join('')}</ol>`);
          listItems = [];
          inOrderedList = false;
        }
        inUnorderedList = true;
        listItems = [];
      }
      listItems.push(`<li>${unorderedMatch[1]}</li>`);
    } else if (orderedMatch) {
      if (!inOrderedList) {
        if (inUnorderedList) {
          // Close unordered list
          processedLines.push(`<ul>${listItems.join('')}</ul>`);
          listItems = [];
          inUnorderedList = false;
        }
        inOrderedList = true;
        listItems = [];
      }
      listItems.push(`<li>${orderedMatch[1]}</li>`);
    } else {
      // Not a list item
      if (inUnorderedList) {
        processedLines.push(`<ul>${listItems.join('')}</ul>`);
        listItems = [];
        inUnorderedList = false;
      } else if (inOrderedList) {
        processedLines.push(`<ol>${listItems.join('')}</ol>`);
        listItems = [];
        inOrderedList = false;
      }
      processedLines.push(line);
    }
  }

  // Close any open lists
  if (inUnorderedList) {
    processedLines.push(`<ul>${listItems.join('')}</ul>`);
  } else if (inOrderedList) {
    processedLines.push(`<ol>${listItems.join('')}</ol>`);
  }

  html = processedLines.join('\n');

  // Handle paragraphs and line breaks
  // First, protect list blocks
  const listPlaceholder = '___LIST_BLOCK___';
  const listBlocks: string[] = [];
  let listIndex = 0;

  html = html.replace(/(<ul>[\s\S]*?<\/ul>|<ol>[\s\S]*?<\/ol>)/g, (match) => {
    listBlocks.push(match);
    return `${listPlaceholder}${listIndex++}${listPlaceholder}`;
  });

  // Split by double line breaks for paragraphs
  const paragraphs = html.split(/\n\n+/);
  const processedParagraphs = paragraphs.map((para) => {
    // Restore list blocks
    para = para.replace(
      new RegExp(`${listPlaceholder}(\\d+)${listPlaceholder}`, 'g'),
      (_, index) => {
        return listBlocks[parseInt(index)] || '';
      },
    );

    // If paragraph contains list tags, don't wrap in <p>
    if (para.includes('<ul>') || para.includes('<ol>')) {
      return para;
    }
    // If paragraph is just whitespace or empty, skip it
    const trimmed = para.trim();
    if (!trimmed) {
      return '';
    }
    // Convert single line breaks to <br /> within paragraphs
    const withBreaks = trimmed.replace(/\n/g, '<br />');
    return `<p>${withBreaks}</p>`;
  });

  html = processedParagraphs.filter((p) => p).join('\n');

  // Clean up empty paragraphs
  html = html.replace(/<p><\/p>/g, '');
  html = html.replace(/<p>\s*<\/p>/g, '');

  return html;
};

/** Palette for citation colour-coding (must match ChatMessages.CITATION_COLORS) */
const CITATION_COLORS = [
  '#2563eb',
  '#059669',
  '#b45309',
  '#dc2626',
  '#7c3aed',
  '#db2777',
  '#0891b2',
  '#65a30d',
];

/** Replaces citation placeholders with clickable in-page links (run after parseMarkdown). Uses hyphens: CITATION-ref-messageId */
const injectCitationLinks = (
  html: string,
  messageId: string,
  getColor: (ref: string) => string,
): string => {
  if (!messageId) return html;
  return html.replace(/CITATION-(\d+)-([a-f0-9-]+)/g, (_, ref) => {
    const color = getColor(ref);
    return `<a href="#" class="citation-ref" data-citation-ref="${ref}" role="button" style="color:${color};font-weight:600;text-decoration:underline;">[${ref}]</a>`;
  });
};

interface MarkdownRendererProps {
  content: string;
  className?: string;
  /** When set, content may contain CITATION-ref-messageId placeholders; they are turned into citation links. */
  citationMessageId?: string;
  /** Returns hex colour for a citation ref (for colour-coding). */
  getCitationColor?: (ref: string) => string;
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  className,
  citationMessageId,
  getCitationColor = (ref) =>
    CITATION_COLORS[parseInt(ref, 10) % CITATION_COLORS.length] ??
    CITATION_COLORS[0],
}) => {
  let html = parseMarkdown(content);
  if (citationMessageId) {
    html = injectCitationLinks(html, citationMessageId, getCitationColor);
  }

  return (
    <MarkdownContainer
      className={className}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};
